import { Controller } from '@hotwired/stimulus';
import { get, supported } from '@github/webauthn-json';
import { postJson } from '../../lib/passkeys';

// Conditional mediation on the identification screen: passkeys show up in the
// email field autofill, without ever asking the user to choose a method.
//
// The challenge is requested when the user reaches the field rather than on
// every load, because it expires well before an idle autofill prompt is
// answered. The field carries autofocus, so reaching it can mean being there
// already: that focus event fires while the page parses, before deferred
// scripts have started Stimulus, and no action listener catches it.
export default class extends Controller {
  static targets = ['email'];
  static values = {
    challengeUrl: String,
    sessionUrl: String,
    enabled: Boolean,
  };

  connect() {
    this.started = false;

    if (this.hasEmailTarget && document.activeElement === this.emailTarget) {
      this.start();
    }
  }

  disconnect() {
    this.abortController?.abort();
  }

  async start() {
    if (this.started) return;

    this.started = true;

    if (!this.enabledValue || !supported()) return;

    const mediationAvailable =
      await window.PublicKeyCredential?.isConditionalMediationAvailable?.();

    if (!mediationAvailable) return;

    this.abortController = new AbortController();

    try {
      const response = await postJson(this.challengeUrlValue, {});

      if (!response.ok) return;

      const credential = await get({
        publicKey: await response.json(),
        mediation: 'conditional',
        signal: this.abortController.signal,
      });

      await this.#openSession(credential);
    } catch (error) {
      // Aborted whenever the user signs in another way — not worth reporting.
      if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
        this.#fallBackToEmailStep();
      }
    }
  }

  async #openSession(credential) {
    const response = await postJson(this.sessionUrlValue, {
      users_passkey: { credential: credential },
    });

    if (!response.ok) {
      this.#fallBackToEmailStep();
      return;
    }

    window.location.assign((await response.json()).redirect_url);
  }

  // The autofill path has no error surface of its own: let the user carry on
  // with the email they are already typing rather than fail silently.
  #fallBackToEmailStep() {
    this.started = false;
    this.abortController?.abort();
    this.abortController = null;
  }
}
